import {
  englishCatalog,
  frenchCatalog,
  germanCatalog,
  italianCatalog,
  type MessageCatalog,
  type MessageId,
} from './catalogs.ts';

export const supportedLocales = ['de', 'fr', 'it', 'en'] as const;
export type SupportedLocale = (typeof supportedLocales)[number];

export interface LocaleResolutionInput {
  readonly search: string;
  readonly browserLanguages: readonly string[];
}

export interface Localization {
  readonly locale: SupportedLocale;
  readonly message: (
    id: MessageId,
    replacements?: Readonly<Record<string, number | string>>,
  ) => string;
  readonly formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  readonly formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string;
  readonly withLocale: (locale: SupportedLocale) => Localization;
}

export const languageChoices = Object.freeze([
  { label: 'DE', locale: 'de' },
  { label: 'FR', locale: 'fr' },
  { label: 'IT', locale: 'it' },
  { label: 'EN', locale: 'en' },
] as const);

const catalogs: Readonly<Record<SupportedLocale, MessageCatalog>> = Object.freeze({
  de: germanCatalog,
  en: englishCatalog,
  fr: frenchCatalog,
  it: italianCatalog,
});

function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && supportedLocales.includes(value as SupportedLocale);
}

function matchSupportedLanguage(value: unknown): SupportedLocale | undefined {
  if (typeof value !== 'string' || value === '' || value !== value.trim()) return undefined;
  try {
    const [canonical] = Intl.getCanonicalLocales(value);
    const primaryLanguage = canonical?.split('-')[0]?.toLowerCase();
    return isSupportedLocale(primaryLanguage) ? primaryLanguage : undefined;
  } catch {
    return undefined;
  }
}

export function resolveLocale({
  search,
  browserLanguages,
}: LocaleResolutionInput): SupportedLocale {
  const queryValues = new URLSearchParams(search).getAll('lang');
  if (queryValues.length === 1) {
    const queryLocale = matchSupportedLanguage(queryValues[0]);
    if (queryLocale !== undefined) return queryLocale;
  }
  for (const language of browserLanguages) {
    const browserLocale = matchSupportedLanguage(language);
    if (browserLocale !== undefined) return browserLocale;
  }
  return 'de';
}

export function getMessageCatalog(locale: SupportedLocale): MessageCatalog {
  return (catalogs as Partial<Record<string, MessageCatalog>>)[locale] ?? germanCatalog;
}

function interpolate(
  template: string,
  replacements: Readonly<Record<string, number | string>> | undefined,
): string {
  if (replacements === undefined) return template;
  return template.replaceAll(/\{([a-z][A-Za-z0-9]*)\}/gu, (placeholder, name: string) => {
    const replacement = replacements[name];
    return replacement === undefined ? placeholder : String(replacement);
  });
}

export function createLocalization(locale: SupportedLocale): Localization {
  const catalog = getMessageCatalog(locale);
  return Object.freeze({
    locale,
    message: (id: MessageId, replacements?: Readonly<Record<string, number | string>>) =>
      interpolate(catalog[id], replacements),
    formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, options).format(value),
    formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, options).format(value),
    withLocale: createLocalization,
  });
}

export type { MessageCatalog, MessageId } from './catalogs.ts';
